import apiInstance from './apiAuth';

/**
 * Tutorial API Service
 * Handles all API calls related to tutorials and tutorial categories
 */

const tutorialApi = {
    /**
     * Get all tutorial categories
     * @returns {Promise} API response with categories
     */
    getCategories: async () => {
        try {
            const response = await apiInstance.get('tutorial-categories/');
            return response;
        } catch (error) {
            console.error('Error fetching tutorial categories:', error);
            throw error;
        }
    },

    /**
     * Create a new tutorial category (admin only)
     * @param {Object} data - Category data
     * @returns {Promise} API response
     */
    createCategory: async (data) => {
        try {
            const response = await apiInstance.post('tutorial-categories/', data);
            return response;
        } catch (error) {
            console.error('Error creating tutorial category:', error);
            throw error;
        }
    },

    /**
     * Get all tutorials with optional filters
     * @param {Object} params - Query parameters (search, category, difficulty, is_featured)
     * @returns {Promise} API response with tutorials
     */
    getTutorials: async (params = {}) => {
        try {
            const response = await apiInstance.get('tutorials/', { params });
            return response;
        } catch (error) {
            console.error('Error fetching tutorials:', error);
            throw error;
        }
    },

    /**
     * Get a single tutorial by ID
     * @param {number} id - Tutorial ID
     * @returns {Promise} API response with tutorial details
     */
    getTutorialById: async (id) => {
        try {
            const response = await apiInstance.get(`tutorials/${id}/`);
            return response;
        } catch (error) {
            console.error(`Error fetching tutorial ${id}:`, error);
            throw error;
        }
    },

    /**
     * Track tutorial view for analytics
     * @param {number} id - Tutorial ID
     * @param {Object} data - View data (completed, watch_duration)
     * @returns {Promise} API response
     */
    trackView: async (id, data = {}) => {
        try {
            const response = await apiInstance.post(`tutorials/${id}/track_view/`, data);
            return response;
        } catch (error) {
            console.error(`Error tracking view for tutorial ${id}:`, error);
            throw error;
        }
    },

    /**
     * Get featured tutorials
     * @returns {Promise} API response with featured tutorials
     */
    getFeaturedTutorials: async () => {
        try {
            const response = await apiInstance.get('tutorials/featured/');
            return response;
        } catch (error) {
            console.error('Error fetching featured tutorials:', error);
            throw error;
        }
    },

    /**
     * Create a new tutorial (admin only)
     * @param {Object} data - Tutorial data
     * @returns {Promise} API response
     */
    createTutorial: async (data) => {
        try {
            const response = await apiInstance.post('tutorials/', data);
            return response;
        } catch (error) {
            console.error('Error creating tutorial:', error);
            throw error;
        }
    },

    /**
     * Update a tutorial (admin only)
     * @param {number} id - Tutorial ID
     * @param {Object} data - Updated tutorial data
     * @returns {Promise} API response
     */
    updateTutorial: async (id, data) => {
        try {
            const response = await apiInstance.put(`tutorials/${id}/`, data);
            return response;
        } catch (error) {
            console.error(`Error updating tutorial ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a tutorial (admin only)
     * @param {number} id - Tutorial ID
     * @returns {Promise} API response
     */
    deleteTutorial: async (id) => {
        try {
            const response = await apiInstance.delete(`tutorials/${id}/`);
            return response;
        } catch (error) {
            console.error(`Error deleting tutorial ${id}:`, error);
            throw error;
        }
    },

    /**
     * Get tutorial analytics (admin only)
     * @returns {Promise} API response with analytics data
     */
    getAnalytics: async () => {
        try {
            const response = await apiInstance.get('tutorials/analytics/');
            return response;
        } catch (error) {
            console.error('Error fetching tutorial analytics:', error);
            throw error;
        }
    }
};

export default tutorialApi;
